/**
 * The "Ustoz" palette, for the places that cannot reach a stylesheet.
 *
 * The values themselves live in `src/index.css` — this module only hands back
 * `var(--…)` references, so a colour is still defined once and still follows
 * light/dark without anything here knowing which theme is active. The two
 * exceptions are documented where they appear: the ConfigProvider needs a real
 * hex for antd's own colour ramp, and Capacitor's status bar needs one for the
 * native chrome. Neither can resolve a CSS variable.
 *
 * See docs/design-language.md §2.
 */

/** Shipped when a centre has not set `branding_settings.primary_color`. */
export const BRAND_DEFAULT_COLOR = '#b5303c';

/**
 * The seven functional hues, in the order the CSS variables declare them.
 * Fixed: never derived from the tenant accent, because a product whose only
 * colour is the customer's colour has no colour identity of its own.
 */
export const FUNCTIONAL_COLORS = [
  'majolica',
  'pistachio',
  'saffron',
  'garnet',
  'plum',
  'terracotta',
  'indigo',
];

export const SUBJECT_COLOR_COUNT = FUNCTIONAL_COLORS.length;

/** `fnColor('saffron')` → `var(--color-fn-saffron)`. */
export const fnColor = (name) => `var(--color-fn-${name})`;

/** The same hue at 12%, which is the only way these are used as a fill. */
export const fnTint = (name) => `var(--color-fn-${name}-tint)`;

/**
 * Map anything that identifies a course onto 1..7, deterministically.
 *
 * Deterministic is the whole point: Matematika must be the same colour on the
 * timetable, the homework list and the phone, on every device and after every
 * deploy. So this is a pure function of the id — no ordering, no index in a
 * fetched array, no palette cursor.
 *
 * @param {string|number|null|undefined} identifier course id, code or name
 * @returns {number} 1..SUBJECT_COLOR_COUNT
 */
export const getSubjectColorIndex = (identifier) => {
  if (identifier === null || identifier === undefined || identifier === '') return 1;
  const text = String(identifier);
  // djb2. Cheap, well-spread over short strings, and — unlike a plain charCode
  // sum — it does not collide on anagrams, so "MAT101" and "TAM101" differ.
  // djb2, which is position-sensitive: a plain charCode sum buckets every
  // anagram together and clusters same-length course codes, which is how a
  // centre ends up with four blue subjects. Seven buckets still collide, and
  // that is fine — two subjects sharing a hue is normal and readable.
  let hash = 5381;
  for (let i = 0; i < text.length; i += 1) {
    hash = ((hash * 33) + text.charCodeAt(i)) >>> 0;
  }
  return (hash % SUBJECT_COLOR_COUNT) + 1;
};

/** The subject's colour, as a CSS variable reference. */
export const getSubjectColor = (identifier) =>
  `var(--color-subject-${getSubjectColorIndex(identifier)})`;

/** The subject's 12% tint, for the fill behind an icon tile or a chip. */
export const getSubjectTint = (identifier) =>
  `var(--color-subject-${getSubjectColorIndex(identifier)}-tint)`;

/**
 * `#b5303c` + 0.2 → `rgba(181, 48, 60, 0.2)`.
 *
 * Only for the ConfigProvider, which needs literal colours for tokens antd
 * cannot derive itself. Anything that renders into the DOM should use a
 * variable instead. Returns the input unchanged if it is not a plain hex —
 * a centre's stored colour is user input and may be anything.
 */
export const withAlpha = (hex, alpha) => {
  const match = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(String(hex).trim());
  if (!match) return hex;
  const digits =
    match[1].length === 3
      ? match[1].split('').map((c) => c + c).join('')
      : match[1];
  const value = parseInt(digits, 16);
  /* eslint-disable no-bitwise */
  const r = (value >> 16) & 255;
  const g = (value >> 8) & 255;
  const b = value & 255;
  /* eslint-enable no-bitwise */
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};
